/**
 * Frontend date helpers.
 * API may return MM-DD-YYYY or YYYY-MM-DDTHH:mm:ss.sssZ (ISO datetime).
 * HTML date inputs need YYYY-MM-DD.
 */

/**
 * Any date format → YYYY-MM-DD  (for <input type="date"> value)
 * Handles:
 *   "MM-DD-YYYY"
 *   "YYYY-MM-DD"
 *   "YYYY-MM-DDTHH:mm:ss.sssZ"  ← backend ISO datetime
 */
export function toInputValue(display) {
  if (!display) return '';
  const s = display.toString();

  // Already YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  // ISO datetime: "2026-03-13T00:00:00.000Z" → "2026-03-13"
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return s.slice(0, 10);

  // MM-DD-YYYY → YYYY-MM-DD
  if (/^\d{2}-\d{2}-\d{4}$/.test(s)) {
    const [m, d, y] = s.split('-');
    return `${y}-${m}-${d}`;
  }

  return s.slice(0, 10); // fallback
}

/**
 * Any date format → MM-DD-YYYY  (for display / API send)
 * Handles:
 *   "YYYY-MM-DD"
 *   "MM-DD-YYYY"
 *   "YYYY-MM-DDTHH:mm:ss.sssZ"
 */
export function toDisplay(val) {
  if (!val) return '';
  const s = val.toString();

  // Already MM-DD-YYYY
  if (/^\d{2}-\d{2}-\d{4}$/.test(s)) return s;

  // ISO datetime or YYYY-MM-DD → slice to YYYY-MM-DD then convert
  const iso = s.slice(0, 10);
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return '';
  return `${m}-${d}-${y}`;
}

/** Returns the nearest upcoming Sunday as MM-DD-YYYY */
// export function getCurrentWeekEnding() {
//   const today = new Date();
//   const day = today.getDay();            // 0=Sun … 6=Sat
//   const diff = day === 0 ? 0 : 7 - day;
//   const sun = new Date(today);
//   sun.setDate(today.getDate() + diff);
//   const y = sun.getFullYear();
//   const m = String(sun.getMonth() + 1).padStart(2, '0');
//   const d = String(sun.getDate()).padStart(2, '0');
//   return `${m}-${d}-${y}`;
// }

/** Returns this week's Saturday (end of Sun–Sat week) as MM-DD-YYYY */
export function getCurrentWeekEnding() {
  const today = new Date();
  const day = today.getDay();        // 0=Sun … 6=Sat
  const diffToSaturday = 6 - day;   // days until this Saturday
  const sat = new Date(today);
  sat.setDate(today.getDate() + diffToSaturday);
  const y = sat.getFullYear();
  const m = String(sat.getMonth() + 1).padStart(2, '0');
  const d = String(sat.getDate()).padStart(2, '0');
  return `${m}-${d}-${y}`;
}

/**
 * Any date format → short day label e.g. "MON"
 * Handles MM-DD-YYYY, YYYY-MM-DD, ISO datetime
 */
export function getDayLabel(dateStr) {
  if (!dateStr) return '';
  const iso = toInputValue(dateStr); // always YYYY-MM-DD
  const date = new Date(`${iso}T00:00:00`); // local time, no UTC shift
  return ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'][date.getDay()] || '';
}

/** "08:00:00" or "08:00" → "8:00 AM" */
export function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}